import { useStores } from 'hooks/useStores'
import type _Account from 'mobx/Account'
import type _User from 'mobx/User'
import type _Infra from 'mobx/Infra'
import { getCurrentChannel } from 'mobx/CouponFlow/utils'
import CouponsStore from 'mobx/Coupons'
import type CouponError from 'mobx/Coupons/errors'
import { isExternal, translateChannel } from 'mobx/Coupons/utils'
import React, { useCallback, useEffect, useState } from 'react'
import { sendCustomEvent } from 'utils/analytics/analytics'
import { codeToLocale } from 'utils/language'
import { inject, observer } from 'mobx-react'
import ExternalModal from './External'
import InternalModal from './Internal'
import { getRulesOfCoupon } from './utils'

const CouponModal: React.FC<{ Account: typeof _Account; User: typeof _User; Infra: typeof _Infra }> = ({ Account, User, Infra }) => {
	const { couponsStore } = useStores()
	const [error, setError] = useState<string | undefined>()

	const coupon = couponsStore.couponModal

	useEffect(() => {
		if (!coupon) {
			return
		}

		try {
			CouponsStore.validateCoupon(coupon)

			sendCustomEvent({
				category: 'popupView',
				action: 'popup_view',
				page_path: window.location.pathname,
				couponId: coupon?.id,
				user: Account?.user?.user?.email || '',
				userFirstName: Account?.user?.user?.firstName || '',
				time: new Date().toISOString(),
			})
		} catch (e) {
			if (!coupon.flags.applied?.value) {
				setError((e as CouponError).text({ channel: translateChannel(getCurrentChannel()) }))
			}
		}
	}, [Account?.user?.user?.email, Account?.user?.user?.firstName, coupon])

	const closeModal = useCallback(() => couponsStore.setCouponModal(null), [couponsStore])

	if (!coupon || Account.signUpPopUp) {
		return null
	}

	const locale = codeToLocale[User?.preferredLanguage ?? Infra?.appParams.l ?? 'en']
	const { signIn, save, applied } = getRulesOfCoupon(coupon, Account.isAuthenticated())
	const external = isExternal(coupon)

	return external ? (
		<ExternalModal coupon={coupon} signIn={signIn} error={error} locale={locale} onClose={closeModal} />
	) : (
		<InternalModal coupon={coupon} signIn={signIn} error={error} locale={locale} save={save} applied={applied} onClose={closeModal} />
	)
}

export default inject('Account', 'Infra', 'User')(observer(CouponModal)) as React.FC
